/**
 * EKYCAdminPanel - 本人確認管理パネル
 *
 * 管理者向けのeKYC申請一覧と審査機能を提供
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiClient,
  ApiError,
  EKYCApplicationListItem,
  EKYCStatus,
} from '../lib/api-client';

type StatusFilter = EKYCStatus | 'all';

export default function EKYCAdminPanel() {
  const { t } = useTranslation();

  // 一覧表示のステート
  const [applications, setApplications] = useState<EKYCApplicationListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const perPage = 10;

  // レビューモーダルのステート
  const [selectedApplication, setSelectedApplication] = useState<EKYCApplicationListItem | null>(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewImages, setReviewImages] = useState<{ idDocument?: string; selfie?: string }>({});
  const [isLoadingReviewImages, setIsLoadingReviewImages] = useState(false);
  const [reviewImagesError, setReviewImagesError] = useState<string | null>(null);

  // 一覧を取得
  const loadApplications = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.listEKYCApplications(
        page,
        perPage,
        statusFilter === 'all' ? undefined : statusFilter
      );
      setApplications(response.items);
      setTotalCount(response.total);
    } catch (err) {
      console.error('Failed to load applications:', err);
      setError('eKYC申請一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
  }, [page, statusFilter]);

  useEffect(() => {
    if (!selectedApplication) {
      setReviewImages({});
      setReviewImagesError(null);
      setIsLoadingReviewImages(false);
      return;
    }

    let isCancelled = false;
    const objectUrls: string[] = [];

    const loadReviewImages = async () => {
      setIsLoadingReviewImages(true);
      setReviewImagesError(null);
      setReviewImages({});

      try {
        const [idDocumentResult, selfieResult] = await Promise.allSettled([
          apiClient.getEKYCApplicationImageObjectUrl(selectedApplication.id, 'id_document'),
          apiClient.getEKYCApplicationImageObjectUrl(selectedApplication.id, 'selfie'),
        ]);

        if (isCancelled) {
          [idDocumentResult, selfieResult].forEach((result) => {
            if (result.status === 'fulfilled') {
              URL.revokeObjectURL(result.value);
            }
          });
          return;
        }

        const nextImages: { idDocument?: string; selfie?: string } = {};
        if (idDocumentResult.status === 'fulfilled') {
          nextImages.idDocument = idDocumentResult.value;
          objectUrls.push(idDocumentResult.value);
        }
        if (selfieResult.status === 'fulfilled') {
          nextImages.selfie = selfieResult.value;
          objectUrls.push(selfieResult.value);
        }

        setReviewImages(nextImages);
        if (!nextImages.idDocument && !nextImages.selfie) {
          setReviewImagesError('本人確認画像はありません');
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to load eKYC images:', err);
          setReviewImagesError('本人確認画像の取得に失敗しました');
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingReviewImages(false);
        }
      }
    };

    loadReviewImages();

    return () => {
      isCancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedApplication?.id]);

  // 手動レビュー（承認）
  const handleApprove = async () => {
    if (!selectedApplication) return;
    setIsReviewing(true);
    setReviewError(null);
    try {
      await apiClient.reviewEKYCApplication(selectedApplication.id, {
        approved: true,
      });
      // 一覧を更新
      await loadApplications();
      // モーダルを閉じる
      setSelectedApplication(null);
    } catch (err) {
      console.error('Failed to approve:', err);
      if (err instanceof ApiError) {
        setReviewError(err.message);
      } else {
        setReviewError('承認処理に失敗しました');
      }
    } finally {
      setIsReviewing(false);
    }
  };

  // 手動レビュー（却下）
  const handleReject = async () => {
    if (!selectedApplication) return;
    if (!rejectionReason.trim()) {
      setReviewError('却下理由を入力してください');
      return;
    }
    setIsReviewing(true);
    setReviewError(null);
    try {
      await apiClient.reviewEKYCApplication(selectedApplication.id, {
        approved: false,
        rejection_reason: rejectionReason,
      });
      // 一覧を更新
      await loadApplications();
      // モーダルを閉じる
      setSelectedApplication(null);
      setRejectionReason('');
    } catch (err) {
      console.error('Failed to reject:', err);
      if (err instanceof ApiError) {
        setReviewError(err.message);
      } else {
        setReviewError('却下処理に失敗しました');
      }
    } finally {
      setIsReviewing(false);
    }
  };

  // ステータスの表示
  const getStatusBadge = (status: EKYCStatus) => {
    switch (status) {
      case 'APPROVED':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">
            {t('ekyc.status.approved', '承認済み')}
          </span>
        );
      case 'SUBMITTED':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
            {t('ekyc.status.submitted', '申請済み')}
          </span>
        );
      case 'PENDING_REVIEW':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
            {t('ekyc.status.pending_review', 'マネージャ承認待ち')}
          </span>
        );
      case 'API_VERIFYING':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-purple-100 text-purple-800">
            {t('ekyc.status.api_verifying', 'API審査中')}
          </span>
        );
      case 'REJECTED':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800">
            {t('ekyc.status.rejected', '却下')}
          </span>
        );
      case 'EXPIRED':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
            {t('ekyc.status.expired', '期限切れ')}
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  // 日時フォーマット
  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ページネーション
  const totalPages = Math.ceil(totalCount / perPage);

  // 審査可能なステータスかどうか（承認待ちのみ）
  const canReview = (status: EKYCStatus) => {
    return status === 'PENDING_REVIEW';
  };

  // プログレスバーのステップ定義
  const progressSteps = ['SUBMITTED', 'API_VERIFYING', 'PENDING_REVIEW', 'APPROVED'] as const;

  // ステータスに応じたプログレス計算
  const getProgressInfo = (item: EKYCApplicationListItem) => {
    const { status, reviewed_at } = item;

    if (status === 'REJECTED') {
      // REJECTEDの場合、どのステップで却下されたか判断
      // reviewed_atがある = マネージャーが却下、ない = API審査で却下
      if (!reviewed_at) {
        // API審査で却下された場合（reviewed_atがない = 管理者レビューなし）
        // API審査ステップ（index 1）で停止
        return { step: 1, isRejected: true };
      } else {
        // マネージャーが却下した場合（reviewed_atがある = 管理者がレビューした）
        // マネージャ承認待ちステップ（index 2）で停止
        return { step: 2, isRejected: true };
      }
    }

    if (status === 'EXPIRED') {
      return { step: 1, isRejected: true };
    }

    const stepIndex = progressSteps.indexOf(status as typeof progressSteps[number]);
    if (stepIndex === -1) return { step: 0, isRejected: false };
    return { step: stepIndex, isRejected: false };
  };

  const getProgressWidthClass = (step: number) => {
    switch (step) {
      case 1:
        return 'w-1/3';
      case 2:
        return 'w-2/3';
      case 3:
        return 'w-full';
      default:
        return 'w-0';
    }
  };

  // プログレスバーのレンダリング
  const renderProgressBar = (item: EKYCApplicationListItem) => {
    const { step, isRejected } = getProgressInfo(item);
    const stepLabels = ['申請済み', 'API審査', 'マネージャ承認待ち', '完了'];

    // 進捗バーの色（処理中は青、完了は緑、却下は赤）
    const getBarColor = () => {
      if (item.status === 'APPROVED') return 'bg-green-500';
      if (item.status === 'REJECTED' || item.status === 'EXPIRED') return 'bg-red-500';
      return 'bg-blue-500';
    };

    // 丸の色
    const getCircleColor = () => {
      if (item.status === 'APPROVED') return 'bg-green-500 border-green-500';
      if (isRejected) return 'bg-red-500 border-red-500';
      // 処理中は青
      return 'bg-blue-500 border-blue-500';
    };

    return (
      <div className="w-full min-w-[180px]">
        {/* ステップラベル */}
        <div className="flex justify-between text-[10px] text-gray-500 mb-1">
          {stepLabels.map((label, idx) => {
            const isRejectedStep = isRejected && idx === step;
            const isAfterRejection = isRejected && idx > step;
            const isPassed = idx < step || (idx === step && !isRejected);

            return (
              <span
                key={label}
                className={`w-1/4 text-center ${
                  isRejectedStep
                    ? 'text-red-600 font-semibold'
                    : isAfterRejection
                      ? 'text-gray-400'
                      : isPassed
                        ? item.status === 'APPROVED'
                          ? 'text-green-600 font-semibold'
                          : 'text-blue-600 font-semibold'
                        : ''
                }`}
              >
                {isRejectedStep ? '✗' : label}
              </span>
            );
          })}
        </div>
        {/* プログレスバーと丸 */}
        <div className="relative px-1.5 h-3">
          <div className="absolute top-1/2 -translate-y-1/2 left-1.5 right-1.5">
            <div className="relative h-1 bg-gray-200 rounded-full overflow-hidden">
              <div className={`${getBarColor()} ${getProgressWidthClass(step)} h-1 rounded-full`}></div>
            </div>
          </div>
          <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex justify-between">
            {stepLabels.map((label, idx) => {
              const isRejectedStep = isRejected && idx === step;
              const isPassed = idx < step;
              const isCurrent = idx === step && !isRejected;

              let circleClass = 'bg-white border-gray-300';

              if (isRejectedStep) {
                circleClass = 'bg-red-500 border-red-500';
              } else if (isPassed) {
                circleClass = getCircleColor();
              } else if (isCurrent) {
                circleClass = item.status === 'APPROVED' ? 'bg-green-500 border-green-500' : 'bg-blue-500 border-blue-500';
              }

              return (
                <div
                  key={label}
                  className={`w-3 h-3 rounded-full border-2 ${circleClass}`}
                ></div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // 結果列のレンダリング
  const renderResult = (item: EKYCApplicationListItem) => {
    const { isRejected } = getProgressInfo(item);

    if (item.status === 'APPROVED') {
      return (
        <div className="text-center">
          <span className="inline-block px-3 py-0.5 text-xs font-bold rounded bg-green-100 text-green-800">
            OK
          </span>
        </div>
      );
    }

    if (isRejected) {
      return (
        <div>
          <div className="text-center">
            <span className="inline-block px-3 py-0.5 text-xs font-bold rounded bg-red-100 text-red-800">
              NG
            </span>
          </div>
          {item.rejection_reason && (
            <div className="mt-1 text-[10px] text-red-600 max-w-[100px] truncate" title={item.rejection_reason}>
              {item.rejection_reason}
            </div>
          )}
        </div>
      );
    }

    // 審査中
    return (
      <div className="text-center">
        <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-gray-100 text-gray-600">
          審査中
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">
          {t('ekyc.admin.title', '本人確認管理')}
        </h2>
        <div className="flex items-center gap-4">
          {/* ステータスフィルター */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              setPage(1);
            }}
            className="py-1.5 px-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">すべて</option>
            <option value="SUBMITTED">申請済み</option>
            <option value="API_VERIFYING">API審査中</option>
            <option value="PENDING_REVIEW">マネージャ承認待ち</option>
            <option value="APPROVED">承認済み</option>
            <option value="REJECTED">却下</option>
            <option value="EXPIRED">期限切れ</option>
          </select>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      {/* 一覧テーブル */}
      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">
                ID
              </th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">
                配信者
              </th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 min-w-[180px]">
                進捗
              </th>
              <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600">
                結果
              </th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">
                申請日時
              </th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">
                審査日時
              </th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">
                審査者
              </th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-2 py-4 text-center text-gray-500">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-2"></div>
                    読み込み中...
                  </div>
                </td>
              </tr>
            ) : applications.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-2 py-4 text-center text-gray-500">
                  該当するデータがありません
                </td>
              </tr>
            ) : (
              applications.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-2 py-1.5 text-xs text-gray-900">
                    {item.id}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-gray-900">
                    <div className="font-medium">{item.display_name || item.username}</div>
                    <div className="text-[10px] text-gray-500">@{item.username}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    {renderProgressBar(item)}
                  </td>
                  <td className="px-2 py-1.5">
                    {renderResult(item)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-gray-500">
                    {formatDateTime(item.created_at)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-gray-500">
                    {item.reviewed_at ? formatDateTime(item.reviewed_at) : '-'}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-gray-500">
                    {item.reviewed_by_username || '-'}
                  </td>
                  <td className="px-2 py-1.5">
                    {canReview(item.status) && (
                      <button
                        onClick={() => setSelectedApplication(item)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-semibold"
                      >
                        審査
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            全 {totalCount} 件中 {(page - 1) * perPage + 1} - {Math.min(page * perPage, totalCount)} 件を表示
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="py-1 px-3 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              前へ
            </button>
            <span className="py-1 px-3 text-sm">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="py-1 px-3 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              次へ
            </button>
          </div>
        </div>
      )}

      {/* 審査モーダル */}
      {selectedApplication && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && setSelectedApplication(null)}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="bg-blue-600 px-6 py-3 rounded-t-lg">
              <h3 className="text-lg font-bold text-white">
                eKYC審査
              </h3>
            </div>

            <div className="p-4 space-y-4">
              {/* 進捗と結果 */}
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  {renderProgressBar(selectedApplication)}
                </div>
                <div className="flex-shrink-0">
                  {renderResult(selectedApplication)}
                </div>
              </div>

              {/* 申請情報 */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="block text-xs font-semibold text-gray-600">申請ID</label>
                  <p className="text-gray-900">{selectedApplication.id}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600">配信者</label>
                  <p className="text-gray-900">
                    {selectedApplication.display_name || selectedApplication.username}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    @{selectedApplication.username} (ID: {selectedApplication.streamer_id})
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600">ステータス</label>
                  {getStatusBadge(selectedApplication.status)}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600">申請日時</label>
                  <p className="text-gray-900">{formatDateTime(selectedApplication.created_at)}</p>
                </div>
              </div>

              {/* 本人確認画像 */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-semibold text-gray-900 mb-3">本人確認画像</h4>
                {isLoadingReviewImages ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    画像を読み込み中...
                  </div>
                ) : reviewImages.idDocument || reviewImages.selfie ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">本人確認書類</label>
                      {reviewImages.idDocument ? (
                        <img
                          src={reviewImages.idDocument}
                          alt="本人確認書類"
                          className="w-full max-h-48 object-contain border border-gray-200 rounded bg-gray-50"
                        />
                      ) : (
                        <div className="h-32 flex items-center justify-center border border-gray-200 rounded text-xs text-gray-500">
                          画像なし
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">顔写真</label>
                      {reviewImages.selfie ? (
                        <img
                          src={reviewImages.selfie}
                          alt="顔写真"
                          className="w-full max-h-48 object-contain border border-gray-200 rounded bg-gray-50"
                        />
                      ) : (
                        <div className="h-32 flex items-center justify-center border border-gray-200 rounded text-xs text-gray-500">
                          画像なし
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    {reviewImagesError || '本人確認画像はありません'}
                  </p>
                )}
              </div>

              {/* 審査操作 */}
              {canReview(selectedApplication.status) && (
                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <h4 className="font-semibold text-gray-900">審査</h4>

                  {/* 却下理由入力 */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">
                      却下理由（却下時のみ）
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="却下する場合は理由を入力してください"
                    />
                  </div>

                  {/* エラー表示 */}
                  {reviewError && (
                    <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                      {reviewError}
                    </div>
                  )}

                  {/* ボタン */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleApprove}
                      disabled={isReviewing}
                      className="py-2 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isReviewing ? '処理中...' : '承認する'}
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={isReviewing}
                      className="py-2 px-6 bg-red-600 hover:bg-red-700 text-white font-semibold rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      却下する
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setSelectedApplication(null);
                  setRejectionReason('');
                  setReviewError(null);
                }}
                className="py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
